import {isNumber, toNumber} from "lodash-es"
import {Feature} from "ol"
import {FeatureLike} from "ol/Feature"
import {Point, LineString, Polygon} from "ol/geom"
import {Style, Text, Fill, Stroke} from "ol/style"
import VectorLayer from "ol/layer/Vector"
import VectorSource from "ol/source/Vector"
import {styleWithStrokedSelect} from "./styles"

export enum LayerId {
	TOWN = "town",
  CITY = "city",
  DEPTH = "depth",
  BOYLAT = "buoy_lateral",
  BOYSAW = "buoy_safe_water",
  BOYSPP = "buoy_special_purpose",
  BCNLAT = "beacon_lateral",
  BCNSPP = "beacon_special_purpose",
  NAVLNE = "navigation_line",
  RECTRC = "recommended_track",
  ACHARE = "anchorage_area",
  OBSTRN = "obstruction",
  ACHBRT = "anchor_berth",
  WRECKS = "wreck",
  DISMAR = "distance_mark",
}

const IconPath = {
  buoys: "/buoys/",
  beacons: "/beacons/",
  polygons: "/polygons/",
  others: "/others/"
}

export const FeatureLayers: {[key: string]: VectorLayer<any>} = {
  [LayerId.TOWN]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<Point>>({features: []}),
    style: (feature) => new Style({
        text: new Text({
          font: '15px sans-serif',
          fill: new Fill({color: '#000000'}),
          text: feature.getProperties()?.name
        })
      }),
    properties: {id: LayerId.TOWN},
    zIndex: 100
  }),
  [LayerId.CITY]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<Point>>({features: []}),
    style: (feature) => new Style({
        text: new Text({
          font: '15px sans-serif',
          fill: new Fill({color: '#000000'}),
          text: feature.getProperties()?.name
        })
      }),
    properties: {id: LayerId.CITY},
    zIndex: 100
  }),
  [LayerId.DEPTH]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<Point>>({features: []}),
    style: (feature) => {
      const depth: number | undefined = feature.getProperties()?.depth
      if (isNumber(depth)) {
        const int = Math.trunc(feature.getProperties()?.depth)
        const decimal = Math.round((depth - int) * 10)
        return [
          new Style({
            text: new Text({
              font: '11px sans-serif',
              fill: new Fill({color: '#000000'}),
              text: int.toString()
            })
          }),
          decimal > 0 ? new Style({
            text: new Text({
              offsetX: String(int).length * 3 + 4,
              offsetY: 4,
              font: '9px sans-serif',
              fill: new Fill({color: '#000000'}),
              text: decimal.toString()
            })
          }) : new Style()
        ]
      }
      return new Style()
    },
    properties: {id: LayerId.DEPTH},
    zIndex: 100
  }),
  [LayerId.BOYLAT]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<Point>>({features: []}),
    style: styleWithStrokedSelect({layerId: LayerId.BOYLAT}),
    properties: {id: LayerId.BOYLAT, iconPath: IconPath.buoys},
    zIndex: 200
  }),
  [LayerId.BOYSAW]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<Point>>({features: []}),
    style: styleWithStrokedSelect({layerId: LayerId.BOYSAW}),
    properties: {id: LayerId.BOYSAW, iconPath: IconPath.buoys},
    zIndex: 200
  }),
  [LayerId.BOYSPP]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<Point>>({features: []}),
    style: styleWithStrokedSelect({layerId: LayerId.BOYSPP}),
    properties: {id: LayerId.BOYSPP, iconPath: IconPath.buoys},
    zIndex: 200
  }),
  [LayerId.BCNLAT]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<Point>>({features: []}),
    style: styleWithStrokedSelect({layerId: LayerId.BCNLAT}),
    properties: {id: LayerId.BCNLAT, iconPath: IconPath.beacons},
    zIndex: 200
  }),
  [LayerId.BCNSPP]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<Point>>({features: []}),
    style: styleWithStrokedSelect({
      layerId: LayerId.BCNSPP,
      getStyleKey: (feature: FeatureLike) => {
        let iconPath = `${IconPath.beacons}${feature.getProperties().icon_name}`
        if (feature.getProperties().icon_name === "speed_limit") {
          const speed = feature.getProperties().information?.split(" ").find((elem: string) => !isNaN(toNumber(elem)))
          if (speed) iconPath = `${iconPath}_${speed}`
        }
        return iconPath
      },
      populateIcon: (svg: Document, feature: FeatureLike) => {
        if (feature.getProperties().icon_name === "speed_limit") {
          const speed = feature.getProperties().information?.split(" ").find((elem: string) => !isNaN(toNumber(elem)))
          if (speed) {
            const digit = document.createElementNS('http://www.w3.org/2000/svg', 'text')
            digit.textContent = speed
            digit.setAttribute('x', '50%')
            digit.setAttribute('y', '59%')
            digit.setAttribute('font-size', '18')
            digit.setAttribute('fill', 'red')
            digit.setAttribute('dominant-baseline', 'middle')
            digit.setAttribute('text-anchor', 'middle')
            svg.documentElement.appendChild(digit)
          }
        }
        return svg
      }
    }),
    properties: {id: LayerId.BCNSPP, iconPath: IconPath.beacons},
    zIndex: 200
  }),
  [LayerId.NAVLNE]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<LineString>>({features: []}),
    style: (feature) => {
      const selected = feature.getProperties().selected
      return new Style({
        stroke: new Stroke({
          color: selected ? [0, 136, 255, 1] : [150, 150, 150, 1],
          width: selected ? 2 : 1,
          lineDash: [4,8]
        })
      })
    },
    properties: {id: LayerId.NAVLNE},
    zIndex: 100
  }),
  [LayerId.RECTRC]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<LineString>>({features: []}),
    style: (feature) => {
      const selected = feature.getProperties().selected
      return new Style({
        stroke: new Stroke({
          color: selected ? [0, 136, 255, 1] : [150, 150, 150, 1],
          width: selected ? 2 : 1,
          lineDash: [4,8]
        })
      })
    },
    properties: {id: LayerId.RECTRC},
    zIndex: 100
  }),
  [LayerId.ACHARE]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<Polygon>>({features: []}),
    style: styleWithStrokedSelect({
      layerId: LayerId.ACHARE,
      loadSelectedIcon: true,
      getIconGeometry: (feature) =>
        (feature as Feature<Polygon>).getGeometry()?.getInteriorPoint(),
      getExtraStyles: (feature, resolution) => {
        const selected = feature.getProperties().selected
        return [
          new Style({
            fill: new Fill({
              color: [173, 216, 230, selected ? 0.7 : 0.05],
            }),
            stroke: new Stroke({
              color: selected ? [0, 136, 255, 1] : [0, 0, 0, 1],
              width: selected ? 2 : 1,
              lineDash: [4,8]
            })
          })
        ]
      }
    }),
    properties: {id: LayerId.ACHARE, iconPath: IconPath.polygons},
    zIndex: 150
  }),
  [LayerId.OBSTRN]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<Point | LineString | Polygon>>({features: []}),
    style: styleWithStrokedSelect({
      layerId: LayerId.OBSTRN,
      loadSelectedIcon: true,
      getStyleKey: (feature: FeatureLike) => {
        return `${IconPath.others}${feature.getProperties().icon_name}_${feature.getGeometry()?.getType()}`
      },
      getIconGeometry: (feature) => {
        switch (feature.getGeometry()?.getType()) {
          case "LineString":
            return (feature as Feature<LineString>).getGeometry() ? new Point((feature as Feature<LineString>).getGeometry()?.getCoordinateAt(0.5)!) : undefined
          case "Polygon":
            return (feature as Feature<Polygon>).getGeometry()?.getInteriorPoint()
        }
      },
      getExtraStyles: (feature, resolution) => {
        const selected = feature.getProperties().selected
        switch (feature.getGeometry()?.getType()) {
          case "Point":
            return []
          case "LineString":
            return [
              new Style({
                stroke: new Stroke({
                  color: selected ? [241, 59, 59, 1] : [0, 0, 0, 1],
                  width: selected ? 2 : 1,
                  lineDash: [4,8]
                })
              })
            ]
          case "Polygon":
            return [
              new Style({
                fill: new Fill({
                  color: [173, 216, 230, selected ? 0.7 : 0.05],
                }),
                stroke: new Stroke({
                  color: selected ? [241, 59, 59, 1] : [0, 0, 0, 1],
                  width: selected ? 2 : 1,
                  lineDash: [4,8]
                })
              })
            ]
        }
        return []
      }
    }),
    properties: {id: LayerId.OBSTRN, iconPath: IconPath.others},
    zIndex: 150
}),
  [LayerId.ACHBRT]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<Point>>({features: []}),
    style: styleWithStrokedSelect({
      layerId: LayerId.ACHBRT,
      loadSelectedIcon: true
    }),
    properties: {
      id: LayerId.ACHBRT,
      iconPath: IconPath.others
    },
    zIndex: 150
  }),
  [LayerId.WRECKS]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<Point>>({features: []}),
    style: styleWithStrokedSelect({
      layerId: LayerId.WRECKS,
      loadSelectedIcon: true,
    }),
    properties: {id: LayerId.WRECKS, iconPath: IconPath.others},
    zIndex: 150
}),
  [LayerId.DISMAR]: new VectorLayer({
    updateWhileAnimating: true,
    source: new VectorSource<Feature<Point>>({features: []}),
    style: (feature, resolution) => {
      const props = feature.getProperties()
      const text = props.information || props.obj_name
      if (props.distance_mark_category && props.distance_mark_category !== "not.installed" && text) {
        return styleWithStrokedSelect({
          layerId: LayerId.DISMAR,
          getStyleKey: (feature: FeatureLike) => {
            return `${IconPath.others}${feature.getProperties().icon_name}_${text}`
          },
          populateIcon: (svg: Document, feature: FeatureLike) => {
            if (text) {
              const digit = document.createElementNS('http://www.w3.org/2000/svg', 'text')
              digit.textContent = text
              digit.setAttribute('x', '50%')
              digit.setAttribute('y', '14')
              digit.setAttribute('font-size', '20')
              digit.setAttribute('fill', 'black')
              digit.setAttribute('dominant-baseline', 'middle')
              digit.setAttribute('text-anchor', 'middle')
              svg.documentElement.appendChild(digit)
            }
            return svg
          }
        })(feature, resolution)
      }
      return new Style({
        text: new Text({
          font: 'bold 25px sans-serif',
          fill: new Fill({
            color: "#000000"
          }),
          text: text,
          padding: [5, 3, 0, 5],
          backgroundStroke: new Stroke({
            color: "#000000",
            width: 1,
            lineCap: 'round',
            lineJoin: 'round'
          }),
        }),
      })
    },
    properties: {id: LayerId.DISMAR, iconPath: IconPath.others},
    zIndex: 150
  }),
}
